/**
 * `useSwitchOrg` — canonical "switch to a different org" handler
 * (W102-F3-S1).
 *
 * Replaces the lighter-touch `useSwitchPartner` (which only invalidates
 * a known set of react-query namespace prefixes) with a full reset:
 *
 *   1. Persist the new active-org id via `TenantContext.setActivePartner`
 *      so the next outbound apiFetch already stamps the correct
 *      `X-Org-Id` header.
 *   2. (Future — W102-F2-S1) Acquire a fresh MSAL token scoped to the
 *      new org. Stubbed today; see the `acquireTokenForOrg` block
 *      below for the wiring point.
 *   3. `queryClient.clear()` — nuke the ENTIRE React-Query cache. We
 *      go bigger than the legacy predicate-invalidate because a single
 *      missed namespace prefix (e.g. a new feature that doesn't yet
 *      route through the allow-list) is enough to silently surface
 *      cross-org data on first paint. Aggressive clear is the safe
 *      default.
 *   4. `resetOrgScopedStores()` — purge every non-react-query store
 *      that registered itself in `stores/reset-org-scoped.ts`.
 *   5. `navigate('/dashboard')` — land on the org-neutral home so the
 *      user doesn't sit on a deep-link page (e.g. `/buildings/<id>`)
 *      whose id belongs to the previous org. The dashboard re-derives
 *      its data from the new tenant scope and the user picks back up
 *      from a clean surface.
 *
 * Awaitable: callers can `await switchOrg(id)` to know the persisted
 * id + cache clear + navigation are done. Useful from the cross-org
 * 403 page (W102-F3-S2) so the user lands on the deep link AFTER the
 * switch completes rather than during it.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '@/contexts/TenantContext';
import { resetOrgScopedStores } from '@/stores/reset-org-scoped';
import type { AcquireTokenForOrgResult } from '@/auth/msal-org';

export interface UseSwitchOrgOptions {
  /**
   * Where to land after the switch. Default `/dashboard` (the spec
   * default). Pass a non-default value when the caller wants the
   * switch + redirect bundled into one user gesture — e.g. cross-org
   * deep-link recovery (W102-F3-S2) navigates back to the original
   * URL after the switch.
   */
  readonly landingPath?: string;
  /**
   * W102-F2-S1 — optional per-org MSAL silent-refresh callback. Fires
   * AFTER `setActivePartner(orgId)` but BEFORE `queryClient.clear()`
   * so a silent-acquire failure can restore the previous active-org
   * id before any cache is dropped. When the result's `status` is
   * `'silent_failed'`, the switch is aborted + the previous active
   * partner is restored; the returned promise rejects with the
   * underlying error message so the caller can toast.
   *
   * Omit in dev-bypass mode where there's no real MSAL flow — the
   * hook then does its existing 4-step chain unchanged.
   */
  readonly acquireTokenForOrg?: (
    orgId: string,
  ) => Promise<AcquireTokenForOrgResult>;
}

export function useSwitchOrg(
  opts: UseSwitchOrgOptions = {},
): (orgId: string) => Promise<void> {
  const { landingPath = '/dashboard', acquireTokenForOrg } = opts;
  const { activePartnerId, setActivePartner } = useTenant();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useCallback(
    async (orgId: string): Promise<void> => {
      // Capture the previous active partner BEFORE we change it so a
      // failing token-acquire can restore. The closure captures
      // `activePartnerId` from the most recent render — sufficient for
      // the manual UI-trigger path (UserMenu / CrossOrgForbidden) which
      // re-renders between each switch.
      const previousOrgId = activePartnerId;

      // 1. Persist active-org id. `apiFetch` reads the registered
      //    getter on every outbound call, so the very next request
      //    already carries `X-Org-Id: <new>`.
      setActivePartner(orgId);

      // 2. W102-F2-S1: per-org MSAL silent-refresh. Fires AFTER the
      //    active-org id flips so the new token is acquired in the
      //    context of the new org; runs BEFORE the cache clear so a
      //    failure can restore the previous id without leaving the
      //    user in a half-cleared state.
      if (acquireTokenForOrg) {
        const result = await acquireTokenForOrg(orgId);
        if (result.status !== 'ok') {
          setActivePartner(previousOrgId);
           
          console.error(
            '[useSwitchOrg] silent token-acquire failed; restored previous org:',
            result.errorMessage,
          );
          throw new Error(
            result.errorMessage ?? 'Silent token acquire failed',
          );
        }
      }

      // 3. Drop the entire React-Query cache. Cross-org data leakage
      //    risk is gone after this.
      queryClient.clear();

      // 4. Reset every registered non-react-query store. Today this
      //    is a no-op (see stores/reset-org-scoped.ts audit notes),
      //    but the wiring is in place for future org-scoped Zustand
      //    stores.
      resetOrgScopedStores();

      // 5. Navigate to org-neutral landing. `navigate` returns void;
      //    we `await Promise.resolve()` so the function as a whole is
      //    awaitable and a chained `then` runs after the cache + state
      //    changes have flushed.
      navigate(landingPath);
      await Promise.resolve();
    },
    [
      activePartnerId,
      setActivePartner,
      queryClient,
      navigate,
      landingPath,
      acquireTokenForOrg,
    ],
  );
}
